import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { getTemplate } from "@/templates/registry";
import { CheckoutIdentityNote } from "@/components/CheckoutIdentityNote";
import {
  Eye, Wand2, ShoppingCart, Loader2, ArrowLeft, Monitor, Tablet, Smartphone,
  Check, Star, Zap, Search, Gauge, Palette, RefreshCw, Globe, ChevronDown, Sparkles,
} from "lucide-react";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const FEATURES = [
  { icon: Smartphone, t: "Fully Responsive", d: "Pixel-perfect on desktop, tablet, and mobile." },
  { icon: Search, t: "SEO Optimized", d: "Semantic markup and meta structure built in." },
  { icon: Zap, t: "Lightning Fast", d: "Optimized assets and lazy-loaded imagery." },
  { icon: Palette, t: "Modern Design", d: "Distinct, on-brand visual identity out of the box." },
  { icon: RefreshCw, t: "Easy to Customize", d: "Clean components you can extend effortlessly." },
  { icon: Globe, t: "Cross-Browser", d: "Consistent experience across every modern browser." },
];
const TECHS = ["React", "Tailwind CSS", "Framer Motion", "Lucide Icons", "Responsive Grid", "Modern CSS"];
const PERF = [["Performance", 98], ["Accessibility", 100], ["Best Practices", 100], ["SEO", 100]];
const PAGES_BY_CAT = {
  Business: ["Home", "Services", "Work", "Pricing", "Contact"],
  Churches: ["Home", "About", "Sermons", "Ministries", "Events", "Give"],
  Restaurants: ["Home", "Menu", "About", "Gallery", "Reservations"],
  Nonprofit: ["Home", "Mission", "Programs", "Impact", "Donate"],
  Construction: ["Home", "Services", "Projects", "About", "Contact"],
  Coaching: ["Home", "About", "Coaching", "Speaking", "Contact"],
  Legal: ["Home", "Practice Areas", "Attorneys", "Results", "Contact"],
  Medical: ["Home", "Services", "Doctors", "Patients", "Contact"],
  Portfolio: ["Home", "Work", "About", "Services", "Contact"],
  Author: ["Home", "Books", "About", "Events", "Contact"],
};
const FAQ = [
  ["What exactly do I get when I buy this template?", "You receive the complete, production-ready source code for the website, a license to use it, and download access from your client portal."],
  ["Can Ask Top Foster customize it for me?", "Yes. Choose \"Customize This Template\" to start our guided project flow — we tailor the design, copy, and features to your brand."],
  ["Is the template responsive?", "Absolutely. Every template is fully responsive across desktop, tablet, and mobile — try the Live Demo device switcher to see it in action."],
  ["Do I need coding knowledge?", "Not if we build it for you. If you buy the template outright, basic web knowledge helps, but the code is clean and well-structured."],
];
const REVIEWS = [
  { n: "Jordan M.", r: 5, t: "Launched in a weekend. The design looked more expensive than anything we could have built ourselves." },
  { n: "Priya K.", r: 5, t: "The live demo sold me instantly — what you see is exactly what you get. Flawless on mobile." },
  { n: "Daniel R.", r: 5, t: "ATF customized it beautifully for our brand. Professional from start to finish." },
];

export default function TemplatePreview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const reg = getTemplate(slug);
  const [dbTpl, setDbTpl] = useState(undefined); // undefined = loading, null = not in DB
  const [buying, setBuying] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!slug) { setDbTpl(null); return; }
    api.get(`/templates/${slug}`).then((r) => setDbTpl(r.data || null)).catch(() => setDbTpl(null));
  }, [slug]);

  // The DB record is the single source of truth (same record Home & Marketplace use).
  // The frontend registry only enriches legacy prototypes when present.
  const tpl = dbTpl
    ? {
        slug: dbTpl.slug || dbTpl.id,
        name: dbTpl.title || reg?.name || "Template",
        description: dbTpl.description || reg?.description || "",
        category: dbTpl.category || reg?.category || "Business",
        price: dbTpl.template_price ?? reg?.price ?? 0,
        customPrice: dbTpl.custom_price ?? reg?.customPrice ?? 0,
        previewImage: dbTpl.preview_image || reg?.previewImage,
        featured: dbTpl.featured ?? reg?.featured,
        isNew: dbTpl.is_new ?? reg?.isNew,
      }
    : (dbTpl === null ? reg : null);
  const downloadable = dbTpl ? !!dbTpl.downloadable : null;

  if (dbTpl === undefined && !reg) {
    return <div className="min-h-[60vh] grid place-items-center text-gray-400" data-testid="preview-loading"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  }
  if (!tpl) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-gray-400" data-testid="preview-not-found">
        Template not found. <button onClick={() => navigate("/marketplace")} className="text-gold ml-2 underline">Back to Our Work</button>
      </div>
    );
  }

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "template", template_id: tpl.slug });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBuying(false); }
  };
  const demo = () => navigate(`/marketplace/demo/${tpl.slug}`);
  const customize = () => navigate(`/start?template=${tpl.slug}`);
  const pages = PAGES_BY_CAT[tpl.category] || ["Home", "About", "Services", "Contact"];

  return (
    <div data-testid="template-preview-page">
      {/* HERO */}
      <section className="border-b border-white/10 relative overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16 relative">
          <button onClick={() => navigate("/marketplace")} data-testid="preview-back-btn" className="inline-flex items-center gap-2 text-gray-400 hover:text-gold transition-colors text-sm mb-8">
            <ArrowLeft size={16} /> Back to Our Work
          </button>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-gold uppercase tracking-[0.2em]">{tpl.category}</span>
                {tpl.featured && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-gold text-navy px-2 py-0.5 font-bold" data-testid="preview-featured"><Sparkles size={10} /> Featured</span>}
                {tpl.isNew && <span className="text-[10px] uppercase tracking-wider bg-emerald-500 text-navy px-2 py-0.5 font-bold" data-testid="preview-new">New</span>}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-white leading-[1.05]" data-testid="preview-name">{tpl.name}</h1>
              <p className="text-lg text-gray-300 font-light mt-5 leading-relaxed" data-testid="preview-description">{tpl.description}</p>
              <div className="flex items-center gap-1 mt-5"><span className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className="fill-gold text-gold" />)}</span><span className="text-gray-400 text-sm ml-2">4.9 · 120+ reviews</span></div>
              <div className="flex flex-wrap gap-3 mt-8">
                <button onClick={demo} data-testid={`live-demo-${tpl.slug}`} className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-6 py-3.5 hover:bg-gold-light transition-colors"><Eye size={17} /> Live Demo</button>
                {downloadable === false ? (
                  <span data-testid="preview-buy-preview-only" className="inline-flex items-center gap-2 border border-white/15 text-gray-400 font-medium px-6 py-3.5 cursor-not-allowed">Purchase availability coming soon</span>
                ) : (
                  <button onClick={buy} disabled={buying} data-testid="preview-buy-btn" className="inline-flex items-center gap-2 border border-white/25 text-white font-medium px-6 py-3.5 hover:border-gold/50 hover:bg-white/5 transition-colors disabled:opacity-60">{buying ? <Loader2 className="animate-spin" size={16} /> : <ShoppingCart size={16} />} Purchase Template</button>
                )}
                <button onClick={customize} data-testid="preview-customize-btn" className="inline-flex items-center gap-2 border border-white/25 text-white font-medium px-6 py-3.5 hover:border-gold/50 hover:bg-white/5 transition-colors"><Wand2 size={16} /> Customize</button>
              </div>
              <p className="text-sm text-gray-400 mt-4" data-testid="preview-price-note">
                Template price <span className="text-white font-heading text-lg">${tpl.price}</span> · one-time
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative group cursor-pointer" onClick={demo}>
              <img src={tpl.previewImage} alt={tpl.name} className="w-full object-cover aspect-[16/11] border border-white/10 shadow-2xl" />
              <div className="absolute inset-0 bg-navy/50 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                <span className="flex items-center gap-2 bg-white text-navy px-5 py-2.5 text-sm font-semibold"><Eye size={15} /> Launch Live Demo</span>
              </div>
              <div className="absolute -bottom-3 -left-3 bg-navy-800 border border-white/10 px-3 py-2 flex items-center gap-3 text-gray-300 text-xs">
                <span className="flex items-center gap-1"><Monitor size={13} className="text-gold" /> Desktop</span>
                <span className="flex items-center gap-1"><Tablet size={13} className="text-gold" /> Tablet</span>
                <span className="flex items-center gap-1"><Smartphone size={13} className="text-gold" /> Mobile</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-20">
        {/* TEMPLATE INFO */}
        <motion.section {...fade} className="grid md:grid-cols-4 gap-6" data-testid="preview-info">
          {[["Category", tpl.category], ["Pages", `${pages.length} included`], ["Responsive", "Desktop · Tablet · Mobile"], ["Stack", "React + Tailwind"]].map(([l, v]) => (
            <div key={l} className="border border-white/10 p-5"><div className="text-xs text-gray-500 uppercase tracking-wide">{l}</div><div className="text-white mt-1.5 font-medium">{v}</div></div>
          ))}
        </motion.section>

        {/* FEATURES */}
        <motion.section {...fade} data-testid="preview-features">
          <h2 className="font-heading text-3xl text-white mb-8">Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.t} className="border border-white/10 p-6 hover:border-gold/40 transition-colors">
                <div className="w-11 h-11 bg-gold/10 text-gold grid place-items-center mb-4"><f.icon size={20} /></div>
                <h3 className="text-white font-medium mb-1.5">{f.t}</h3><p className="text-gray-400 text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* TECHNOLOGIES + PERFORMANCE */}
        <div className="grid md:grid-cols-2 gap-12">
          <motion.section {...fade} data-testid="preview-technologies">
            <h2 className="font-heading text-3xl text-white mb-6">Technologies</h2>
            <div className="flex flex-wrap gap-2.5">{TECHS.map((t) => <span key={t} className="border border-white/15 text-gray-200 px-4 py-2 text-sm">{t}</span>)}</div>
          </motion.section>
          <motion.section {...fade} data-testid="preview-performance">
            <h2 className="font-heading text-3xl text-white mb-6 flex items-center gap-2"><Gauge size={24} className="text-gold" /> Performance</h2>
            <div className="grid grid-cols-2 gap-4">
              {PERF.map(([l, v]) => (
                <div key={l} className="border border-white/10 p-4 flex items-center justify-between"><span className="text-gray-300 text-sm">{l}</span><span className="font-heading text-2xl text-emerald-400">{v}</span></div>
              ))}
            </div>
          </motion.section>
        </div>

        {/* INCLUDED PAGES */}
        <motion.section {...fade} data-testid="preview-pages">
          <h2 className="font-heading text-3xl text-white mb-6">Included Pages</h2>
          <div className="flex flex-wrap gap-3">{pages.map((p) => <span key={p} className="inline-flex items-center gap-2 border border-white/10 px-4 py-2.5 text-gray-200 text-sm"><Check size={15} className="text-gold" /> {p}</span>)}</div>
        </motion.section>

        {/* REVIEWS */}
        <motion.section {...fade} data-testid="preview-reviews">
          <h2 className="font-heading text-3xl text-white mb-8">Reviews</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {REVIEWS.map((rv) => (
              <div key={rv.n} className="border border-white/10 p-6">
                <div className="flex gap-0.5 mb-3">{Array.from({ length: rv.r }).map((_, i) => <Star key={i} size={14} className="fill-gold text-gold" />)}</div>
                <p className="text-gray-300 text-sm leading-relaxed">“{rv.t}”</p>
                <div className="text-gray-500 text-sm mt-4">— {rv.n}</div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section {...fade} data-testid="preview-faq">
          <h2 className="font-heading text-3xl text-white mb-6">Frequently Asked</h2>
          <div className="border border-white/10 divide-y divide-white/10">
            {FAQ.map(([q, a], i) => (
              <div key={q}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left text-white hover:bg-white/5 transition-colors" data-testid={`faq-${i}`}>
                  <span className="font-medium">{q}</span><ChevronDown size={18} className={`text-gold shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-5 text-gray-400 text-sm leading-relaxed">{a}</div>}
              </div>
            ))}
          </div>
        </motion.section>

        {/* PRICING */}
        <motion.section {...fade} data-testid="preview-pricing">
          <h2 className="font-heading text-3xl text-white mb-8 text-center">Pricing</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="border border-white/10 p-8">
              <div className="text-gray-400 text-sm uppercase tracking-wide">Template License</div>
              <div className="font-heading text-4xl text-white mt-2">${tpl.price}</div>
              <p className="text-gray-400 text-sm mt-2">One-time · full source code + license.</p>
              <ul className="mt-6 space-y-2.5 text-sm text-gray-300">{["Complete source code", "Commercial license", "Portal download access", "Free minor updates"].map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-gold shrink-0" /> {f}</li>)}</ul>
              {downloadable !== false && <CheckoutIdentityNote className="mt-5" />}
              {downloadable === false ? (
                <div data-testid="preview-buy-preview-only-2" className="w-full mt-7 text-center bg-white/5 border border-white/10 text-gray-400 font-medium py-3.5">Purchase availability coming soon</div>
              ) : (
                <button onClick={buy} disabled={buying} data-testid="preview-buy-btn-2" className="w-full mt-7 flex items-center justify-center gap-2 bg-gold text-navy font-semibold py-3.5 hover:bg-gold-light transition-colors disabled:opacity-60">{buying ? <Loader2 className="animate-spin" size={16} /> : <ShoppingCart size={16} />} Purchase Template</button>
              )}
            </div>
            <div className="border border-gold/40 p-8 relative">
              <span className="absolute -top-3 left-8 bg-gold text-navy text-xs font-bold px-3 py-1 uppercase tracking-wide">Recommended</span>
              <div className="text-gray-400 text-sm uppercase tracking-wide">Custom Build</div>
              <div className="font-heading text-4xl text-gold mt-2">${tpl.customPrice?.toLocaleString()}</div>
              <p className="text-gray-400 text-sm mt-2">We tailor it to your brand, end to end.</p>
              <ul className="mt-6 space-y-2.5 text-sm text-gray-300">{["Brand-matched design & copy", "AI-assisted proposal & concierge", "Managed project + client portal", "Launch & handoff support"].map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-gold shrink-0" /> {f}</li>)}</ul>
              <button onClick={customize} data-testid="preview-customize-btn-2" className="w-full mt-7 flex items-center justify-center gap-2 border border-gold text-gold font-semibold py-3.5 hover:bg-gold/10 transition-colors"><Wand2 size={16} /> Customize This Template</button>
            </div>
          </div>
        </motion.section>

        {/* FINAL CTA */}
        <motion.section {...fade} className="text-center border-t border-white/10 pt-16">
          <h2 className="font-heading text-3xl md:text-4xl text-white">See it in action</h2>
          <p className="text-gray-400 mt-3">Launch the interactive demo and experience the full site.</p>
          <button onClick={demo} data-testid="preview-live-demo-cta" className="inline-flex items-center gap-2 mt-7 bg-gold text-navy font-semibold px-8 py-4 hover:bg-gold-light transition-colors"><Eye size={18} /> Launch Live Demo</button>
        </motion.section>
      </div>
    </div>
  );
}
